import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const C = {
  bgPage:     '#0f172a', bgPanel: '#1e293b', bgPanel2: '#273449',
  borderSoft: '#334155', textMain: '#f1f5f9', textMuted: '#94a3b8',
  textDim:    '#64748b',
  success:    '#22c55e', successBg: 'rgba(34, 197, 94, 0.12)',
  warning:    '#f59e0b', warningBg: 'rgba(245, 158, 11, 0.12)',
  danger:     '#ef4444', dangerBg:  'rgba(239, 68, 68, 0.12)',
  info:       '#60a5fa', infoBg:    'rgba(96, 165, 250, 0.12)',
  purple:     '#a78bfa', purpleBg:  'rgba(167, 139, 250, 0.12)',
  teal:       '#2dd4bf', tealBg:    'rgba(45, 212, 191, 0.12)',
};

function Kpi({ label, value, suffix, color, sub, testId }) {
  return (
    <div style={{
      background: C.bgPanel, border: `1px solid ${C.borderSoft}`,
      borderRadius: 8, padding: 16, borderLeft: `4px solid ${color || C.info}`,
    }} data-testid={testId}>
      <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || C.textMain, marginTop: 4 }}>
        {value}{suffix && <span style={{ fontSize: 14, color: C.textMuted, marginLeft: 4 }}>{suffix}</span>}
      </div>
      {sub && <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function HoursBar({ rows, max = 12 }) {
  if (!rows?.length) return <div style={{ padding: 24, color: C.textMuted, textAlign: 'center' }}>Sin datos en el periodo</div>;
  const actualMax = Math.max(max, ...rows.map(r => r.day_hours || 0));
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', gap: 2,
      height: 180, padding: 12, overflowX: 'auto',
      background: C.bgPanel, border: `1px solid ${C.borderSoft}`, borderRadius: 8,
    }}>
      {rows.map((r, i) => {
        const h       = r.day_hours || 0;
        const pctH    = Math.max(2, (h / actualMax) * 160);
        const color   = r.auto ? C.warning : h > 12 ? C.danger : h >= 8 ? C.success : C.info;
        const wd      = new Date(r.date).getDay();
        const weekend = wd === 0 || wd === 6;
        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 26px' }}
               title={`${r.date} • ${h.toFixed(2)}h • ${r.shift} • ${r.estado}`}>
            <div style={{
              width: 20, height: pctH, background: color, borderRadius: 3,
              opacity: weekend ? 0.5 : 1,
            }} data-testid={`hours-bar-${r.date}`}/>
            <div style={{ fontSize: 9, color: C.textDim, marginTop: 4, writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}>
              {r.date.slice(5)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BreakdownBars({ items, color, title, testId }) {
  if (!items?.length) return null;
  const total = items.reduce((s, i) => s + i.count, 0);
  return (
    <div style={{ background: C.bgPanel, border: `1px solid ${C.borderSoft}`, borderRadius: 8, padding: 16 }} data-testid={testId}>
      <h4 style={{ margin: '0 0 12px', color: C.textMain, fontSize: 13, fontWeight: 600 }}>{title}</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.slice(0, 10).map(it => {
          const pct = Math.round((it.count / total) * 100);
          return (
            <div key={it.name}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.textMuted, marginBottom: 2 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{it.name}</span>
                <span>{it.count} ({pct}%)</span>
              </div>
              <div style={{ height: 6, background: C.bgPanel2, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: color || C.info }}/>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function EmpleadoProductividadPage() {
  const { empId } = useParams();
  const navigate  = useNavigate();
  const { token } = useAuth();
  const [days, setDays] = useState(90);
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const [pdfLoading,  setPdfLoading]  = useState(false);

  // Date range derived from `days` (last N days ending yesterday)
  const computeRange = (n) => {
    const end = new Date();
    end.setDate(end.getDate() - 1);
    const start = new Date(end);
    start.setDate(start.getDate() - (n - 1));
    const fmt = d => d.toISOString().slice(0, 10);
    return { fecha_ini: fmt(start), fecha_fin: fmt(end) };
  };

  const downloadXlsx = async () => {
    setXlsxLoading(true);
    try {
      const { fecha_ini, fecha_fin } = computeRange(days);
      const url = `${BACKEND}/api/export/kpis.xlsx?empleado=${encodeURIComponent(empId)}&fecha_ini=${fecha_ini}&fecha_fin=${fecha_fin}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `fichajes_${empId}_${fecha_ini}_${fecha_fin}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert(`Error al descargar Excel: ${e.message}`);
    }
    setXlsxLoading(false);
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const { fecha_ini, fecha_fin } = computeRange(days);
      const url = `${BACKEND}/api/reports/pdf?empleado=${encodeURIComponent(empId)}&fecha_ini=${fecha_ini}&fecha_fin=${fecha_fin}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 404) throw new Error('No hay datos en el periodo seleccionado');
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `reporte_ia_${empId}_${fecha_ini}_${fecha_fin}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert(`Error al generar PDF: ${e.message}`);
    }
    setPdfLoading(false);
  };

  useEffect(() => {
    if (!token) return;
    setLoad(true); setError(null);
    fetch(`${BACKEND}/api/employees/${empId}/productividad?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(setData)
      .catch(e => setError(String(e)))
      .finally(() => setLoad(false));
  }, [empId, days, token]);

  const emp = data?.employee || {};
  const sum = data?.summary  || {};

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={() => navigate(-1)} data-testid="back-button" style={{
          background: C.bgPanel2, border: `1px solid ${C.borderSoft}`, color: C.textMain,
          padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
        }}>← Volver</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.textMain }} data-testid="empleado-nombre">
            {emp.apellidos ? `${emp.apellidos}, ${emp.nombre || ''}` : (emp.nombre || emp.numero_personal || empId)}
          </h1>
          <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>
            <span>ID <strong style={{ color: C.textMain }}>{empId}</strong></span>
            {emp.desc_puesto && <span> • {emp.desc_puesto}</span>}
            {emp.desc_centro_coste && <span> • {emp.desc_centro_coste}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {[30, 90, 180, 365].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`days-${d}`}
              style={{
                padding: '6px 12px',
                background: days === d ? C.info : C.bgPanel2,
                color: days === d ? C.bgPage : C.textMain,
                border: `1px solid ${days === d ? C.info : C.borderSoft}`,
                borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: days === d ? 600 : 400,
              }}
            >
              {d}d
            </button>
          ))}
          <div style={{ width: 1, height: 22, background: C.borderSoft, margin: '0 4px' }} />
          <button
            onClick={downloadXlsx}
            disabled={xlsxLoading || loading}
            data-testid="download-xlsx-btn"
            title="Descargar fichajes del periodo en Excel (sin nombres - RGPD)"
            style={{
              padding: '6px 12px',
              background: xlsxLoading ? C.bgPanel2 : C.success, color: '#0f172a',
              border: 'none', borderRadius: 6, cursor: xlsxLoading ? 'wait' : 'pointer',
              fontSize: 12, fontWeight: 600, opacity: (xlsxLoading || loading) ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {xlsxLoading ? 'Generando...' : 'Excel'}
          </button>
          <button
            onClick={downloadPdf}
            disabled={pdfLoading || loading}
            data-testid="download-pdf-btn"
            title="Reporte PDF generado con IA (Claude Sonnet 4.6)"
            style={{
              padding: '6px 12px',
              background: pdfLoading ? C.bgPanel2 : C.purple, color: '#0f172a',
              border: 'none', borderRadius: 6, cursor: pdfLoading ? 'wait' : 'pointer',
              fontSize: 12, fontWeight: 600, opacity: (pdfLoading || loading) ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 13h6M9 17h6"/>
            </svg>
            {pdfLoading ? 'Analizando...' : 'Reporte IA (PDF)'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: C.dangerBg, border: `1px solid ${C.danger}`, color: C.danger, padding: 12, borderRadius: 6 }}>
          Error: {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 80, textAlign: 'center', color: C.textMuted }}>Cargando…</div>
      ) : data && (
        <>
          {/* KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
            <Kpi label="Días trabajados"  value={sum.total_days}  color={C.info}    sub={`de ${data.window.weekdays} laborables`} testId="kpi-days" />
            <Kpi label="Horas totales"    value={sum.total_hours} suffix="h"        color={C.success}  testId="kpi-total-hours" />
            <Kpi label="Media diaria"     value={sum.avg_hours}   suffix="h"        color={C.info}     testId="kpi-avg-hours" />
            <Kpi label="Asistencia"       value={sum.attendance_pct} suffix="%"     color={sum.attendance_pct >= 90 ? C.success : sum.attendance_pct >= 70 ? C.warning : C.danger} testId="kpi-attendance" />
            <Kpi label="Fichajes correctos" value={sum.correct_pct} suffix="%"      color={sum.correct_pct >= 90 ? C.success : C.warning} testId="kpi-correct" />
            <Kpi label="Auto-cerrados"    value={sum.auto_closed}                    color={sum.auto_closed > 5 ? C.danger : C.warning} testId="kpi-auto" />
            <Kpi label="Jornadas >12h"    value={sum.excessive_days}                 color={sum.excessive_days > 0 ? C.danger : C.success} testId="kpi-excessive" />
            <Kpi label="Fines de semana"  value={sum.weekend_days}                   color={C.purple}   testId="kpi-weekend" />
            {sum.punctuality_avg_min !== null && (
              <Kpi
                label="Puntualidad media"
                value={(sum.punctuality_avg_min > 0 ? '+' : '') + sum.punctuality_avg_min}
                suffix="min"
                color={Math.abs(sum.punctuality_avg_min) <= 10 ? C.success : sum.punctuality_avg_min > 0 ? C.warning : C.info}
                sub={sum.punctuality_avg_min > 0 ? 'tarde de media' : 'antes de media'}
                testId="kpi-punctuality"
              />
            )}
          </div>

          {/* Hours chart */}
          <div>
            <h3 style={{ margin: '0 0 8px', color: C.textMain, fontSize: 15, fontWeight: 600 }}>Horas por día</h3>
            <HoursBar rows={data.hours_by_day} />
            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: C.textMuted, marginTop: 8, flexWrap: 'wrap' }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: C.info, borderRadius: 2, marginRight: 4 }}/> &lt;8h</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: C.success, borderRadius: 2, marginRight: 4 }}/> 8-12h</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: C.danger, borderRadius: 2, marginRight: 4 }}/> &gt;12h</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: C.warning, borderRadius: 2, marginRight: 4 }}/> Auto-cerrado</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: C.textMuted, borderRadius: 2, marginRight: 4, opacity: 0.5 }}/> Fin de semana</span>
            </div>
          </div>

          {/* Breakdowns */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
            <BreakdownBars items={data.breakdown.shifts}  color={C.info}    title="Turnos"   testId="breakdown-shifts" />
            <BreakdownBars items={data.breakdown.estados} color={C.warning} title="Estados"  testId="breakdown-estados" />
            <BreakdownBars items={data.breakdown.centros} color={C.purple}  title="Centros"  testId="breakdown-centros" />
          </div>

          {/* Daily detail table */}
          <div style={{ background: C.bgPanel, border: `1px solid ${C.borderSoft}`, borderRadius: 8, overflow: 'hidden' }}>
            <h3 style={{ margin: 0, padding: 16, color: C.textMain, fontSize: 15, fontWeight: 600, borderBottom: `1px solid ${C.borderSoft}` }}>
              Detalle diario ({data.hours_by_day.length} registros)
            </h3>
            <div style={{ overflowX: 'auto', maxHeight: 500 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ position: 'sticky', top: 0, background: C.bgPanel2 }}>
                  <tr>
                    {['Fecha','Turno','Entrada','Salida','Horas','Estado','Centro'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', color: C.textMuted, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.hours_by_day.slice().reverse().map((r, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                      <td style={{ padding: '8px 12px', color: C.textMain }}>{r.date}</td>
                      <td style={{ padding: '8px 12px', color: C.textMain }}>{r.shift}</td>
                      <td style={{ padding: '8px 12px', color: C.textMain }}>{r.first_ts?.slice(11, 16) || '—'}</td>
                      <td style={{ padding: '8px 12px', color: C.textMain }}>{r.last_ts?.slice(11, 16) || '—'}</td>
                      <td style={{ padding: '8px 12px', color: r.day_hours > 12 ? C.danger : r.day_hours >= 8 ? C.success : C.textMain, fontWeight: 600 }}>
                        {(r.day_hours || 0).toFixed(2)}h
                      </td>
                      <td style={{ padding: '8px 12px', color: r.auto ? C.warning : r.estado === 'Correcto' ? C.success : C.textMain }}>
                        {r.estado}
                      </td>
                      <td style={{ padding: '8px 12px', color: C.textDim, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.centro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
